/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useEffect, useMemo, useState } from "react";
import { Article, Combined } from "../types/article";
import { useEDSData } from "./useEDSData";
import { useSalesStopData } from "./useSalesStopData";
import { joinProductArticleIds } from "../tools";
import { useTaskLinksByMedia } from "./useTaskLinksByMedia";
import { EdsData, SalesStopData } from "../types/article";

const getTruncatedTime = (date: string): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
interface ArticleWithEdsAndStops extends Article {
  eds?: EdsData;
  salesStop?: SalesStopData;
}

export function useCombinedArticles(
  countryId: string,
  storeId: string,
  mediaId: string,
  articles: ArticleWithEdsAndStops[] | undefined
) {
  // The same article objects are referenced in articles, combinedArticles and
  // articleMap.
  const [combinedArticles, setCombinedArticles] = useState<
    Combined<Article>[] | undefined
  >(articles);

  // Map to find article objects by productArticleId (ex aaa.bbb.cc). Used for
  // salesStop and eds since it always is the same for all articles in the list.
  // The same article can be in media or groups at the same time.
  const [productArticleIdMap, setProductArticleIdMap] = useState<
    Map<string, Combined<Article>[]>
  >(new Map([]));

  // Map to find articles by id. The id is probably media internal to be able to
  // separate articles in media and different groups. Tasks can be place on articles
  // and/or groups.
  const [productIdMap, setProductIdMap] = useState<
    Map<string, Combined<Article>>
  >(new Map([]));
  useEffect(() => {
    if (articles) {
      const newProductArticleIdMap: Map<string, Combined<Article>[]> =
        new Map();
      const newProductIdMap: Map<string, Combined<Article>> = new Map();
      for (const article of articles) {
        if (article.createdAt) {
          // eslint-disable-next-line react-hooks/immutability
          article.createdAtDate = new Date(article.createdAt);
        }
        const oldArticles =
          newProductArticleIdMap.get(article.productArticleId) ?? [];
        newProductArticleIdMap.set(article.productArticleId, [
          ...oldArticles,
          article,
        ]);
        newProductIdMap.set(article.id, article);
      }
      setProductArticleIdMap(newProductArticleIdMap);
      setProductIdMap(newProductIdMap);
    }
  }, [articles]);
  const productArticleIdsString = useMemo(
    () => joinProductArticleIds(articles ?? []),
    [articles]
  );

  const { data: edsList } = useEDSData(countryId, productArticleIdsString);

  useEffect(() => {
    for (const eds of edsList ?? []) {
      if (eds.validToDateTime) {
        // eslint-disable-next-line react-hooks/immutability
        eds.validToDate = getTruncatedTime(eds.validToDateTime);
        eds.validToDateTime = eds.validToDate.toJSON();
      }
      if (eds.validFromDateTime) {
        eds.validFromDate = getTruncatedTime(eds.validFromDateTime);
        eds.validFromDateTime = eds.validFromDate.toJSON();
      }
      // check if the eds.productArticleId has parentArticleId in articles, if yes then assign eds to that article
      const edsArticles = productArticleIdMap.get(eds.productArticleId) ?? [];

      for (const edsArticle of edsArticles) {
        if (edsArticle.parentArticleCombinationNumber) {
          const matches = articles?.filter(
            (a) =>
              a.productArticleId === edsArticle.parentArticleCombinationNumber
          );

          if (matches && matches.length > 0) {
            const today = new Date();
            // Find the match with validToDateTime closest to today
            const closestMatch = matches.reduce((prev, curr) => {
              const prevDate = prev?.eds?.validToDateTime
                ? new Date(prev?.eds?.validToDateTime)
                : today;
              const currDate = curr?.eds?.validToDateTime
                ? new Date(curr?.eds?.validToDateTime)
                : today;
              return Math.abs(currDate.getTime() - today.getTime()) <
                Math.abs(prevDate.getTime() - today.getTime())
                ? curr
                : prev;
            });
            closestMatch.eds = eds;
          }
        }

        edsArticle.eds = eds;
      }
    }
    setCombinedArticles(articles ? [...articles] : articles);
  }, [productArticleIdMap, articles, edsList]);

  const { data: salesStopList } = useSalesStopData(
    storeId,
    productArticleIdsString
  );
  useEffect(() => {
    for (const salesStop of salesStopList ?? []) {
      const saleStopArticles =
        productArticleIdMap.get(salesStop.productArticleId) ?? [];

      for (const saleStopArticle of saleStopArticles) {
        saleStopArticle.salesStop = salesStop;
        const match = articles?.filter(
          (a) =>
            a.productArticleId ===
            saleStopArticle.parentArticleCombinationNumber
        );

        if (match && match.length > 0) {
          match.forEach((article) => {
            article.salesStop = salesStop;
          });
        }
      }
    }
    setCombinedArticles(articles ? [...articles] : articles);
  }, [productArticleIdMap, articles, salesStopList]);

  const { data: tasks } = useTaskLinksByMedia(mediaId);
  useEffect(() => {
    for (const task of tasks ?? []) {
      const tasksArticle = productIdMap.get(task.articleId)!;
      if (tasksArticle) {
        if (!tasksArticle.tasks) {
          tasksArticle.tasks = [];
        }
        tasksArticle.tasks.push(task);
      }
    }
    setCombinedArticles(articles ? [...articles] : articles);
  }, [productIdMap, articles, tasks]);

  return combinedArticles;
}
